import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash.debounce'
import SvgIcon from 'Components/SvgIcon'
import useOnClickOutside from 'Hooks/useOnClickOutside'
import useBodyOverflowHidden from 'Hooks/useBodyOverflowHidden'
import useListKeyboardInteraction from 'Hooks/useListKeyboardInteraction'
import useEscapeOnEsc from 'Hooks/useEscapeOnEsc'


import {
  _Container, _DropdownBox,
  _TitleBox, _BlurBg, _TitleLeftIcon, _BtnContainer, _MenuBtn,
} from './styles'

const Dropdown = (
  {
    className = '',
    dropdownClass = '',
    titleContainerClass = '',
    children, title,
    blurBackground = true,
    titleLeftIconName = '',
    gaFromPage = '',
    gaFromComponent = '',
    googleAnalyticsEvent,
    closeOnSelect = true,
    scrollableParentClass,
    childClassName,
    onKeyboardSelect = () => {},
    size = 'medium',
    onResetBtnClick,
    additionalBtns,
  }) => {
  const wrapperRef = useRef(null)
  const childContainerRef = useRef(null)
  const [showDropdown, updateShowDropdown] = useState(false)
  const [focused, updateFocusState] = useState(false)
  // in order to disable parent scroll when dropdown is open
  if(scrollableParentClass) useBodyOverflowHidden(showDropdown, scrollableParentClass)
  if(childClassName) useBodyOverflowHidden(showDropdown)
  const closeDropdownOnSelect = debounce(() => updateShowDropdown(false), 50)
  useOnClickOutside(wrapperRef, debounce(() => updateShowDropdown(false), 100))
  //
  // dropdown close on esc button click
  useEscapeOnEsc({
    enableExitOnEsc: showDropdown,
    onEsc: () => updateShowDropdown(false),
  })

  // handle keyboard interaction on dropdown item
  if(childClassName) {
    useListKeyboardInteraction({
      enableKeyboardInteraction: showDropdown || focused,
      childClassName: childClassName || `.${_DropdownBox.styledComponentId} > *`,
      isDropdownOpen: showDropdown,
      updateOpenState: (state) => updateShowDropdown(state),
      onFocusOut: () => {
        updateShowDropdown(false)
        updateFocusState(false)
      },
      onSelect: (index) => {
        onKeyboardSelect(index)
        updateShowDropdown(false)
      },
    })
  }
  const applyCancelBtns = () => (
    <_BtnContainer>
      <_MenuBtn onClick={onResetBtnClick}>Cancel</_MenuBtn>
      <_MenuBtn onClick={() => updateShowDropdown(false)}>Apply</_MenuBtn>
    </_BtnContainer>
  )

  return (
    <_Container
      showDropdown={showDropdown}
      className={`${className} dropdownContainer ${showDropdown ? 'dropdownVisible' : ''}`}
      hasLeftIcon={titleLeftIconName}
      size={size}
      data-test-id="dropdownContainer"
      onClick={(e) => {
        e.preventDefault()
        if(!showDropdown && gaFromPage && gaFromComponent) {
          googleAnalyticsEvent({
            category: gaFromPage,
            action: `[click][${gaFromComponent} from ${gaFromPage}]`,
          })
        }
        if(showDropdown && !childContainerRef.current.contains(e.target)) {
          updateShowDropdown(false)
        } else if(!showDropdown) { // open select
          debounce(() => updateShowDropdown(true), 50)()
        }
      }}
      onFocus={() => updateFocusState(true)}
      onBlur={() => updateFocusState(false)}
    >

      <_TitleBox className={`${titleContainerClass} dropdownTitle`}>
        {
          titleLeftIconName && (
            <_TitleLeftIcon>
              <SvgIcon name={titleLeftIconName} width="16" height="16" />
            </_TitleLeftIcon>
          )
        }
        {
          title && <div htmlFor="title" >{title}</div>
        }

      </_TitleBox>
      <SvgIcon name="#icon-dropdown-arrow" width="12" height="12" />
      <div
        ref={childContainerRef}
        onClick={closeOnSelect ? closeDropdownOnSelect : null}
        className={'dropdownCont'}
      >
        {showDropdown && (
          // eslint-disable-next-line jsx-a11y/role-supports-aria-props
          <_DropdownBox
            ref={wrapperRef}
            data-test-id="dropdownListBox"
            role="listbox"
            aria-haspopup
            aria-expanded
            className={`${dropdownClass} dropdown`}
            // onClick={e => {e.stopPropagation()}
          >
            {children}
            {additionalBtns ? applyCancelBtns() : null}
          </_DropdownBox>
        )}
      </div>
      {blurBackground && showDropdown && (
        <_BlurBg />
      )}
    </_Container>
  )
}

Dropdown.propTypes = {
  dropdownClass: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  blurBackground: PropTypes.bool,
  titleLeftIconName: PropTypes.string,
  googleAnalyticsEvent: PropTypes.func,
  onKeyboardSelect: PropTypes.func,
  gaFromPage: PropTypes.string,
  gaFromComponent: PropTypes.string,
  titleContainerClass: PropTypes.string,
  scrollableParentClass: PropTypes.arrayOf(PropTypes.string),
  childClassName: PropTypes.string,
  size: PropTypes.string,
  closeOnSelect: PropTypes.bool,
  mobileBrowser: PropTypes.bool,
  additionalBtns: PropTypes.bool,
  onResetBtnClick: PropTypes.func,
}

export default Dropdown
